import './UfStrip.css';
import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { FlipList } from '../../components/FlipList';
import { UfTile, type UfTileFill } from '../../components/UfTile';
import { t } from '../../copy/strings';
import { fmtPctAdaptive } from '../../format/numbers';
import { placeName } from '../../format/places';
import type { RaceModel } from '../../model/governors';
import type { UfCode } from '../../model/types';
import { clearMapLink, navigate, setMapLink } from '../../state/actions';
import { type Party, partyOf } from '../../theme/parties';

/** Origem do destaque no mapa pedido pela grade (§9.11.4: só quem pediu limpa). */
export const UF_STRIP_LINK_SOURCE = 'governors-strip';

/**
 * Fundo do selo pela fase (§11.1 `UfStrip`, tabela de dados): sem votos → "sem dados" com textura; apurando → `m0`/`m1`
 * do líder; definida → `m3` do vencedor. Empate exato apurando (sem líder) → neutro, como o mapa (`tieIndex`, §9.5).
 */
export function tileFill(r: RaceModel): UfTileFill {
  if (r.phase === 'no_data') return 'nodata';
  if (r.side === null) return 'plain';
  return r.tone;
}

/** Partido da cor do selo e da linha: o do vencedor ou do líder; nenhum sem lado. */
export function raceParty(r: RaceModel): Party | undefined {
  return r.side === null ? undefined : partyOf(r.candidates[r.side]);
}

/**
 * Nome acessível do selo (§11.1 `UfStrip` item 7): "{UF}, {X} {pct}" apurando; "{UF}, {X} eleito, {pct}" com resultado
 * do TSE; "{UF}, {X} com a eleição definida, {pct}" só com a definição matemática; "{UF}, aguardando" sem votos. {UF} é o
 * nome por extenso (como nos marcadores do mapa). Empate exato apurando: o primeiro do par pela ordem de número (regra
 * fixa, independente de quem tem mais votos).
 */
export function stripAria(r: RaceModel): string {
  const UF = placeName(r.uf);
  if (r.phase === 'no_data') return t('gov.strip.aria.waiting', { UF });
  const side = r.side ?? 0;
  const c = r.candidates[side];
  const pX = fmtPctAdaptive(r.shares[side], r.shares[0], r.shares[1]);
  const vars = { UF, X: c.display_name, 'X.gender': c.gender, pX };
  if (r.phase === 'decided') {
    return t(r.decision?.kind === 'tse' ? 'gov.strip.aria.elected' : 'gov.strip.aria.decided', vars);
  }
  return t('gov.strip.aria', vars);
}

export interface UfStripProps {
  /** Disputas na ordem da grade (`stripOrder`). */
  races: readonly RaceModel[];
  /** UF aberta (`aria-current="true"`). */
  current: UfCode | null;
  /** Replay: reordena sem FLIP (§11.1 bloco 4). */
  replay: boolean;
}

/** Abre a UF mantendo a aba Governadores (§11.1 bloco 5): `#/governadores/{uf}`. */
const openRace = (uf: UfCode) => navigate({ office: 'governor', place: { kind: 'uf', uf } });

/**
 * Grade de selos das UFs em disputa (§11.1 `UfStrip`): 9 colunas, um `UfTile` em `<button>` por UF com 2º turno, na
 * ordem definidas → em apuração/sem dados (eleitorado decrescente no grupo). Com o ponteiro ou o foco dentro, a ordem
 * fica congelada (FlipList `freezeOnHover`); ao sair, a nova ordem entra com FLIP (sem animação no replay e com
 * movimento reduzido, §15.6.3). Hover (mouse) e foco destacam a UF no mapa sem navegar (§9.11.4); clique abre a UF.
 */
export function UfStrip({ races, current, replay }: UfStripProps) {
  // a grade pode sumir com o destaque aceso (troca de aba, de modo, de cena): apaga o que acendeu
  useEffect(() => () => clearMapLink(UF_STRIP_LINK_SOURCE), []);
  const items = races.map((r) => ({ key: r.uf, race: r }));
  const highlight = (uf: UfCode) => setMapLink({ kind: 'place', code: uf }, UF_STRIP_LINK_SOURCE);
  const clear = () => clearMapLink(UF_STRIP_LINK_SOURCE);
  return (
    // biome-ignore lint/a11y/useSemanticElements: §11.1 e §15.4.2 pedem role="group"; <fieldset> exige <legend> e quebra a grade
    <div class="uf-strip" data-testid="uf-strip" role="group" aria-label={t('governors.strip.label')}>
      <FlipList
        as="div"
        items={items}
        freezeOnHover
        replay={replay}
        class="uf-strip__grid"
        itemClass="uf-strip__item"
        renderItem={({ race }) => {
          const onEnter = (e: JSX.TargetedPointerEvent<HTMLSpanElement>) => {
            if (e.pointerType === 'mouse') highlight(race.uf);
          };
          return (
            <span
              class="uf-strip__cell"
              data-uf={race.uf}
              data-phase={race.phase}
              onPointerEnter={onEnter}
              onPointerLeave={clear}
              onFocusIn={() => highlight(race.uf)}
              onFocusOut={clear}
            >
              <UfTile
                code={race.uf}
                fill={tileFill(race)}
                party={raceParty(race)}
                size="strip"
                label={stripAria(race)}
                current={current === race.uf}
                onClick={() => openRace(race.uf)}
              />
            </span>
          );
        }}
      />
    </div>
  );
}
